import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { InfoIcon, PlusIcon } from 'lucide-react';
import { FilterChip } from '../../components/FilterChip';
import { HealthConsentModal } from '../../components/HealthConsentModal';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card, SectionTitle } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { Switch } from '../../components/ui/Switch';
import { findBiomarker } from '../../data/biomarkers';
import { useBiomarkers } from '../../contexts/BiomarkersContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useSettings } from '../../contexts/SettingsContext';
import { formatIsoDate, todayIso } from '../../utils/dates';
import { convertLabValue, formatLabValue } from '../../utils/units';

/**
 * MOB/Profile/Health/Biomarker — e.g. Total Testosterone.
 * Records and converts values; never classifies or interprets them.
 */
export function BiomarkerDetail() {
  const {
    biomarkerId = ''
  } = useParams();
  const {
    t,
    tl,
    locale
  } = useLocale();
  const navigate = useNavigate();
  const bio = useBiomarkers();
  const settings = useSettings();
  const def = findBiomarker(biomarkerId);
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState(def?.canonicalUnit ?? '');
  const [date, setDate] = useState(todayIso());
  const [source, setSource] = useState('');
  const [error, setError] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  if (!def) {
    return <div className="px-2 pb-2" data-id="element-2997" data-mp="2asqooqv9vtzd">
        <PageHeader title={t('bio.title')} showBack data-id="element-2998" data-mp="2fq0p11k79q1z" />
        <EmptyState title={t('bio.none')} body={t('bio.future')} data-id="element-2999" data-mp="2frk1si6ndiz0" />
      </div>;
  }
  const units = Object.keys(def.factors);
  const displayUnit = bio.unitFor(def.id);
  const records = bio.recordsFor(def.id);
  const latest = records[0];
  const isPublic = bio.isPublic(def.id);
  const show = (canonical: number) => formatLabValue(convertLabValue(canonical, def.canonicalUnit, displayUnit, def.factors), def.decimals[displayUnit] ?? 0, locale);
  const save = () => {
    const parsed = Number(value.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError(true);
      return;
    }
    bio.addRecord({
      biomarkerId: def.id,
      value: parsed,
      unit,
      date,
      source: source.trim() || undefined
    });
    setAdding(false);
    setValue('');
    setSource('');
    setError(false);
  };
  return <div className="px-2 pb-2" data-id="element-3000" data-mp="1hbaaewo6i756">
      <PageHeader title={tl(def.name)} subtitle={t('bio.subtitle')} showBack data-id="element-3001" data-mp="1ha8w8jbv94j8" />

      {/* Latest result */}
      <Card tone="dark" className="rounded-panel p-3" data-id="element-3002" data-mp="1d95d9nyiusds">
        <p className="bee-overline text-[8.5px] font-bold text-ondark" data-id="element-3003" data-mp="3j4pzvq8j5fyk">{t('bio.last')}</p>
        {latest ? <>
            <p className="mt-1 flex items-baseline gap-1" data-id="element-3004" data-mp="3f4fvuj4aszmc">
              <span className="bee-num text-[36px] font-bold leading-none text-sand" data-id="element-3005" data-mp="34zhcc81tu20l">
                {show(latest.value)}
              </span>
              <span className="text-[13px] font-medium text-ondark" data-id="element-3006" data-mp="391ufcop6vlto">{displayUnit}</span>
            </p>
            <p className="mt-1 text-[12px] text-ondark" data-id="element-3007" data-mp="2v6sbs87te8k1">
              {formatIsoDate(latest.date, locale)}
              {latest.source ? ` · ${latest.source}` : ''}
            </p>
          </> : <p className="mt-1 text-[14px] text-sand" data-id="element-3008" data-mp="2q9igwd1np4ot">{t('bio.none')}</p>}

        <div className="mt-3 flex items-center gap-2" data-id="element-3009" data-mp="2q7ffyxyytr5b">
          <span className="text-[11.5px] text-ondark" data-id="element-3010" data-mp="2casjewaxd2zx">{t('bio.displayUnit')}</span>
          <div className="flex gap-1" data-id="element-3011" data-mp="2h8bvpqo2b5p8">
            {units.map(item => <button key={item} type="button" aria-pressed={displayUnit === item} onClick={() => bio.setUnit(def.id, item)} className={`min-h-[32px] rounded-pill border px-2 text-[11.5px] font-medium transition-colors duration-150 ease-brand ${displayUnit === item ? 'border-gold bg-gold text-deep' : 'border-sand/20 text-sand hover:border-gold/55'}`} data-id="element-3012" data-mp="aie1vycam0k1">
                {item}
              </button>)}
          </div>
        </div>
      </Card>

      {/* Visibility — private by default, consent to publish */}
      <Card className="mt-2 p-2" data-id="element-3013" data-mp="1snkde45foccg">
        <div className="flex items-center gap-2" data-id="element-3014" data-mp="1nura7kpm5c2h">
          <div className="min-w-0 flex-1" data-id="element-3015" data-mp="1nsy3y6iwwtv0">
            <p className="text-[13.5px] font-bold text-deep" data-id="element-3016" data-mp="1iv6cteq3dbrv">{t('bio.public')}</p>
            <p className="mt-[2px] text-[11px] leading-snug text-muted" data-id="element-3017" data-mp="1dx4vozc87mj1">{t('bio.publicHint')}</p>
          </div>
          <Switch checked={isPublic} label={t('bio.public')} onChange={next => {
          if (next) setConsentOpen(true);else bio.setPublic(def.id, false);
        }} data-id="element-3018" data-mp="19vrlye60iz6u" />
        </div>
        {isPublic && !settings.privacy.publicBiomarkers && <p className="mt-2 border-t border-sand-line pt-2 text-[11.5px] leading-snug text-gold-deep" data-id="element-3019" data-mp="3lhk2rvolawa6">
            {t('bio.masterOff')}
          </p>}
      </Card>

      {/* Add result */}
      <section className="mt-3" data-id="element-3020" data-mp="3ui8gepa2mumj">
        {adding ? <Card className="p-2" data-id="element-3021" data-mp="3ugxb2l0z9etb">
            <SectionTitle data-id="element-3022" data-mp="2xttttw2666q7">{t('bio.add')}</SectionTitle>
            <div className="flex gap-2" data-id="element-3023" data-mp="2xr7lhu7gfn6x">
              <label className="block min-w-0 flex-1" data-id="element-3024" data-mp="31tuj6bqt42c1">
                <span className="bee-overline text-[9px] font-bold text-muted" data-id="element-3025" data-mp="31s9n2hougbc2">{t('bio.value')}</span>
                <input inputMode="decimal" value={value} onChange={event => {
              setValue(event.target.value);
              setError(false);
            }} aria-invalid={error} aria-describedby={error ? 'bio-value-error' : undefined} className={`bee-num mt-1 h-11 w-full rounded-card border bg-white/80 px-2 text-[14px] text-deep focus:border-deep/40 ${error ? 'border-ink' : 'border-sand-line'}`} data-id="element-3026" data-mp="36rw0os8l3tle" />
              </label>
              <div className="shrink-0" data-id="element-3027" data-mp="3r5qvffbkl1kt">
                <span className="bee-overline text-[9px] font-bold text-muted" data-id="element-3028" data-mp="3r71w369pxt7p">{t('bio.unit')}</span>
                <div className="mt-1 flex gap-1" data-id="element-3029" data-mp="33gz7j050j5du">
                  {units.map(item => <FilterChip key={item} label={item} active={unit === item} onClick={() => setUnit(item)} data-id="element-3030" data-mp="2yi4mrsnhj6r3" />)}
                </div>
              </div>
            </div>
            {error && <p id="bio-value-error" role="alert" className="mt-1 text-[11.5px] text-ink" data-id="element-3031" data-mp="rosi03ozkk2u">
                {t('bio.invalid')}
              </p>}

            <label className="mt-2 block" data-id="element-3032" data-mp="v8b4i4b36msn">
              <span className="bee-overline text-[9px] font-bold text-muted" data-id="element-3033" data-mp="v79hd1gsq9uk">{t('bio.date')}</span>
              <input type="date" value={date} max={todayIso()} onChange={event => setDate(event.target.value)} className="mt-1 h-11 w-full rounded-card border border-sand-line bg-white/80 px-2 text-[13.5px] text-deep focus:border-deep/40" data-id="element-3034" data-mp="zbh9l8mgq7xj" />
            </label>
            <label className="mt-2 block" data-id="element-3035" data-mp="1iyexlhqnf45e">
              <span className="bee-overline text-[9px] font-bold text-muted" data-id="element-3036" data-mp="1ixc9tiomhhtp">{t('bio.source')}</span>
              <input value={source} onChange={event => setSource(event.target.value)} placeholder={t('bio.lab')} className="mt-1 h-11 w-full rounded-card border border-sand-line bg-white/80 px-2 text-[13.5px] text-deep placeholder:text-muted focus:border-deep/40" data-id="element-3037" data-mp="1474xzlt4ex1z" />
            </label>

            <div className="mt-3 flex gap-2" data-id="element-3038" data-mp="1nf4wpjq6uol">
              <Button variant="outline" fullWidth onClick={() => setAdding(false)} data-id="element-3039" data-mp="1o88jhne142i">
                {t('bio.cancel')}
              </Button>
              <Button variant="gold" fullWidth onClick={save} data-id="element-3040" data-mp="3o16m56zgg42o">
                {t('bio.save')}
              </Button>
            </div>
          </Card> : <Button variant="outline" size="lg" fullWidth onClick={() => setAdding(true)} data-id="element-3041" data-mp="kl9bkwxdsu3t">
            <PlusIcon size={16} data-id="element-3042" data-mp="kiviuuq6btzf" />
            {t('bio.add')}
          </Button>}
      </section>

      {/* History */}
      {records.length > 0 && <section className="mt-3" data-id="element-3043" data-mp="bj8fw4hsqg49">
          <SectionTitle data-id="element-3044" data-mp="bme65n25vo1k">{t('bio.history')}</SectionTitle>
          <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-3045" data-mp="6kotk2zaj2bd">
            {records.map(record => <li key={record.id} className="flex items-center gap-2 border-b border-sand-line py-2 last:border-b-0" data-id="element-3046" data-mp="2wc3m63elpfvu">
                <span className="min-w-0 flex-1" data-id="element-3047" data-mp="16upw0ktkf9jj">
                  <span className="block text-[12.5px] font-medium text-deep" data-id="element-3048" data-mp="16rtw4qy5qasg">
                    {formatIsoDate(record.date, locale)}
                  </span>
                  <span className="mt-[2px] block truncate text-[11px] text-muted" data-id="element-3049" data-mp="1ftjp417othsc">
                    {record.source ? `${record.source} · ` : ''}
                    {t('bio.entered', {
                unit: record.enteredUnit
              })}
                  </span>
                </span>
                <span className="bee-num shrink-0 text-[14px] font-bold text-deep" data-id="element-3050" data-mp="1uipwwdklcgbu">
                  {show(record.value)}{' '}
                  <span className="text-[11px] font-medium text-muted" data-id="element-3051" data-mp="1zjndilpyd2b8">{displayUnit}</span>
                </span>
              </li>)}
          </ul>
        </section>}

      <p className="mt-3 flex items-start gap-2 rounded-card border border-sand-line bg-sand-deep/50 p-2 text-[11.5px] leading-snug text-deep" data-id="element-3052" data-mp="552zgrw5050t">
        <InfoIcon size={14} className="mt-[1px] shrink-0 text-gold-deep" aria-hidden="true" data-id="element-3053" data-mp="97z9hu7m2st5" />
        {t('bio.disclaimer')}
      </p>

      <button type="button" onClick={() => navigate('/perfil/editar')} className="mt-2 px-1 text-[11.5px] font-medium text-gold-deep underline decoration-gold/40 underline-offset-2" data-id="element-3054" data-mp="3a5hb6oxk86eb">
        {t('preview.edit')}
      </button>

      <HealthConsentModal subject={consentOpen ? tl(def.name) : null} onClose={() => setConsentOpen(false)} onAccept={() => {
      bio.setPublic(def.id, true);
      setConsentOpen(false);
    }} data-id="element-3055" data-mp="3k589l1p8r5jo" />
    </div>;
}